// src/app/routes/ProtectedRoute.tsx
import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, To } from 'react-router-dom';
import {
  getIsLoggedIn,
  getHasPassedTest,
  getEmailVerified,
  getAuthInitialized,
} from '@store/auth/auth-selectors';
import { RootState } from '@store/store';
import { DelayedLoader } from '@components/shared/globalLoader/DelayedLoader';

type AccessLevel =
  | 'authOnly'
  | 'testPassedOnly'
  | 'unauthOnly'
  | 'authNotTest'
  | 'authVerifiedNotTest'
  | 'authNotVerified'
  | 'authTestRerunAllowed'
  | 'authVerifiedTestOrRerun';


interface ProtectedRouteProps {
  element: React.JSX.Element;
  redirectTo: To;
  accessLevel?: AccessLevel;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  element,
  redirectTo,
  accessLevel = 'authOnly',
}) => {
  const isLoggedIn = useSelector(getIsLoggedIn);
  const hasPassedTest = useSelector(getHasPassedTest);
  const hasVerified = useSelector(getEmailVerified);
  let accessGranted: boolean;

  const initialized = useSelector<RootState, boolean>(getAuthInitialized);

  if (isLoggedIn && !initialized) {
    return <DelayedLoader />;
  }

  switch (accessLevel) {
    case 'authOnly':
      accessGranted = isLoggedIn;
      break;
    case 'authTestRerunAllowed': {
      const params = new URLSearchParams(window.location.search);
      const isRenew = params.get('mode') === 'renew';
      const allowFlag = sessionStorage.getItem('allowRenewSurvey') === '1';
      accessGranted = isLoggedIn && hasVerified && hasPassedTest && isRenew && allowFlag;
      break;
    }
    case 'authVerifiedTestOrRerun': {
      const params = new URLSearchParams(window.location.search);
      const isRenew = params.get('mode') === 'renew';
      const allowFlag = sessionStorage.getItem('allowRenewSurvey') === '1';

      // если первый тест (без renew) → доступен после email verify
      // если повторный (renew) → нужен allowRenewSurvey
      accessGranted =
        isLoggedIn &&
        hasVerified &&
        ((hasPassedTest && isRenew && allowFlag) || !hasPassedTest);

      break;
    }
    case 'authNotVerified':
      accessGranted = isLoggedIn && !hasVerified;
      break;
    case 'authVerifiedNotTest':
      accessGranted = isLoggedIn && hasVerified && !hasPassedTest;
      break;
    case 'authNotTest':
      accessGranted = isLoggedIn && !hasPassedTest;
      break;
    case 'testPassedOnly':
      accessGranted = isLoggedIn && hasPassedTest;
      break;
    case 'unauthOnly':
      accessGranted = !isLoggedIn;
      break;
    default:
      accessGranted = false;
  }

  return accessGranted ? (
    element
  ) : (
    <div style={{ minHeight: '100vh' }}>
      <Navigate to={redirectTo} replace />
    </div>
  );
};

export default ProtectedRoute;
